<template>
  <div class="labels-management-page">
    <!-- Заголовок -->
    <div class="page-header">
      <h1>🏷️ Управление этикетками</h1>
      <p class="page-subtitle">Добавление, редактирование и удаление этикеток товаров</p>
    </div>

    <!-- Форма добавления/редактирования -->
    <div class="form-card">
      <h2>{{ isEditing ? '✏️ Редактировать этикетку' : '➕ Добавить новую этикетку' }}</h2>
      
      <div class="form-grid">
        <div class="form-group">
          <label for="labelName">📝 Название этикетки</label>
          <input
            id="labelName"
            v-model="currentLabel.label_name"
            type="text"
            placeholder="Например: Новинка, Распродажа"
            class="input-field"
            :class="{ 'error': errors.label_name }"
          />
          <span v-if="errors.label_name" class="error-text">{{ errors.label_name }}</span>
        </div>

        <div class="form-group">
          <label for="labelColor">🎨 Цвет этикетки</label>
          <div class="color-selection">
            <input
              id="labelColor"
              v-model="currentLabel.color"
              type="color"
              class="color-picker"
            />
            <span class="color-value">{{ currentLabel.color }}</span>
          </div>
          <div class="color-presets">
            <button
              v-for="preset in colorPresets"
              :key="preset"
              :style="{ backgroundColor: preset }"
              class="color-preset"
              @click="currentLabel.color = preset"
            />
          </div>
        </div>
      </div>

      <div class="form-actions">
        <button
          class="btn btn-primary"
          :disabled="!isFormValid || isSubmitting"
          @click="isEditing ? updateLabel() : createLabel()"
        >
          <span v-if="isSubmitting" class="btn-loading"></span>
          <span v-else>{{ isEditing ? '💾 Сохранить' : '➕ Создать' }}</span>
        </button>
        <button
          v-if="isEditing"
          class="btn btn-secondary"
          @click="resetForm()"
          :disabled="isSubmitting"
        >
          ❌ Отмена
        </button>
      </div>
    </div>

    <!-- Список этикеток -->
    <div class="labels-list-card">
      <div class="list-header">
        <h2>📋 Все этикетки</h2>
        <div class="search-box">
          <input
            v-model="searchQuery"
            type="text"
            placeholder="🔍 Поиск этикеток..."
            class="search-input"
            @input="onSearchInput"
          />
        </div>
      </div>

      <div v-if="loading" class="loading-state">
        <div class="spinner"></div>
        <p>Загрузка этикеток...</p>
      </div>

      <div v-else-if="filteredLabels.length === 0 && !searchQuery" class="empty-state">
        <span class="empty-emoji">📭</span>
        <p>Нет этикеток для отображения</p>
        <button class="btn btn-secondary" @click="resetForm()">
          ➕ Создать первую этикетку
        </button>
      </div>

      <div v-else-if="filteredLabels.length === 0 && searchQuery" class="empty-state">
        <span class="empty-emoji">🔍</span>
        <p>Ничего не найдено по запросу "{{ searchQuery }}"</p>
      </div>

      <div v-else class="labels-grid">
        <div
          v-for="label in filteredLabels"
          :key="label.id"
          class="label-card"
          :style="{ borderLeftColor: label.color }"
        >
          <div class="label-info">
            <div class="label-badge" :style="{ backgroundColor: label.color }">
              <span class="label-emoji">🏷️</span>
              <span class="label-name">{{ label.label_name }}</span>
            </div>
            <div class="label-color">
              <span class="color-dot" :style="{ backgroundColor: label.color }"></span>
              <span class="color-code">{{ label.color }}</span>
            </div>
            <div class="label-stats">
              <span class="stats-item">📦 {{ label.product_count || 0 }} товаров</span>
            </div>
          </div>
          
          <div class="label-actions">
            <button
              class="btn-action edit-btn"
              @click="editLabel(label)"
              title="Редактировать"
              :disabled="isSubmitting"
            >
              ✏️
            </button>
            <button
              class="btn-action delete-btn"
              @click="confirmDelete(label)"
              title="Удалить"
              :disabled="label.product_count > 0 || isSubmitting"
            >
              🗑️
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Модальное окно подтверждения удаления -->
    <div v-if="showDeleteModal" class="modal-overlay">
      <div class="modal-content">
        <div class="modal-header">
          <h3>⚠️ Подтверждение удаления</h3>
        </div>
        <div class="modal-body">
          <p>Вы уверены, что хотите удалить этикетку <strong>"{{ labelToDelete?.label_name }}"</strong>?</p>
          <p v-if="labelToDelete?.product_count > 0" class="warning-text">
            ⚠️ Эта этикетка используется в {{ labelToDelete?.product_count }} товарах и не может быть удалена
          </p>
        </div>
        <div class="modal-actions">
          <button 
            class="btn btn-secondary" 
            @click="cancelDelete"
            :disabled="isSubmitting"
          >
            Отмена
          </button>
          <button
            class="btn btn-danger"
            @click="deleteLabel"
            :disabled="labelToDelete?.product_count > 0 || isSubmitting"
          >
            <span v-if="isSubmitting" class="btn-loading"></span>
            <span v-else>Удалить</span>
          </button>
        </div>
      </div>
    </div>

    <!-- Уведомления -->
    <div v-if="notification.show" class="notification" :class="notification.type">
      <span class="notification-emoji">{{ notification.emoji }}</span>
      <span class="notification-text">{{ notification.message }}</span>
      <button class="notification-close" @click="notification.show = false">✖️</button>
    </div>
  </div>
</template>

<script>
import api from '@/api/api'; // Импортируем ваш экземпляр axios

export default {
  name: 'LabelsManagementPage',
  
  data() {
    return {
      labels: [],
      currentLabel: {
        id: null,
        label_name: '',
        color: '#3B82F6'
      },
      searchQuery: '',
      loading: false,
      isEditing: false,
      isSubmitting: false,
      errors: {},
      showDeleteModal: false,
      labelToDelete: null,
      notification: {
        show: false,
        message: '',
        type: 'success',
        emoji: '✅'
      },
      searchTimeout: null,
      colorPresets: [
        '#3B82F6', // Синий
        '#10B981', // Зеленый
        '#EF4444', // Красный
        '#F59E0B', // Желтый
        '#8B5CF6', // Фиолетовый
        '#EC4899', // Розовый
        '#6366F1', // Индиго
        '#14B8A6', // Бирюзовый
      ]
    }
  },

  computed: {
    filteredLabels() {
      if (!this.searchQuery.trim()) return this.labels
      const query = this.searchQuery.toLowerCase()
      return this.labels.filter(label => 
        label.label_name.toLowerCase().includes(query) ||
        label.color.toLowerCase().includes(query)
      )
    },

    isFormValid() {
        return this.currentLabel.label_name.trim() !== '' && 
             this.currentLabel.color.trim() !== ''
    }
  },

  mounted() {
    // Загрузка этикеток при монтировании компонента
    this.fetchLabels()

    
    // Отображаем кнопку назад и привязываем обработчик
    if (window.Telegram.WebApp) {
        window.Telegram.WebApp.BackButton.onClick(this.backButtonOnClick);
        window.Telegram.WebApp.BackButton.show();
    }
},

beforeUnmount() {
    // Убираем обработчик кнопки назад при уходе со страницы
        if (window.Telegram.WebApp) {
            window.Telegram.WebApp.BackButton.offClick(this.backButtonOnClick);
            window.Telegram.WebApp.BackButton.hide();
        }
},

methods: {
    // Отображаем кнопку назад (Telegram mini app)
    backButtonOnClick() {
        if (window.Telegram.WebApp) {
            window.history.back();
        }
    },

    // Загрузка этикеток с сервера
    async fetchLabels() {
      this.loading = true
      try {
        console.log('Загружаем этикетки...')
        const response = await api.get('/bot_managment/labels', {
          params: {
            search: this.searchQuery || undefined
          }
        })
        
        console.log('Ответ от сервера:', response)
        
        if (response.data) {
          this.labels = Array.isArray(response.data) ? response.data : []
          console.log('Загружено этикеток:', this.labels.length)
        }
      } catch (error) {
        console.error('Ошибка при загрузке этикеток:', error)
        console.error('Детали ошибки:', {
          message: error.message,
          response: error.response,
          request: error.request
        })
        this.showNotification('Ошибка при загрузке этикеток', 'error', '❌')
        this.labels = []
      } finally {
        this.loading = false
      }
    },

    onSearchInput() {
      // Дебаунс для поиска
      clearTimeout(this.searchTimeout)
      this.searchTimeout = setTimeout(() => {
        this.fetchLabels()
      }, 500)
    },

    validateForm() {
      this.errors = {}
      
      if (!this.currentLabel.label_name.trim()) {
        this.errors.label_name = 'Название обязательно'
      } else if (this.currentLabel.label_name.length > 100) {
        this.errors.label_name = 'Название слишком длинное (макс. 100 символов)'
      }

      if (!this.currentLabel.color.trim()) {
        this.errors.color = 'Цвет обязателен'
      } else if (!/^#([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})$/.test(this.currentLabel.color)) {
        this.errors.color = 'Цвет должен быть в формате #RRGGBB или #RGB'
      }

      return Object.keys(this.errors).length === 0
    },

    // Создание новой этикетки
    async createLabel() {
      if (!this.validateForm()) return

      this.isSubmitting = true
      try {
        console.log('Создаем этикетку:', this.currentLabel)
        const response = await api.post('/bot_managment/labels', this.currentLabel)
        console.log('Ответ от сервера:', response)
        
        const newLabel = response.data
        this.labels.unshift(newLabel)
        this.showNotification('Этикетка успешно создана', 'success', '✅')
        this.resetForm()
      } catch (error) {
        console.error('Ошибка при создании этикетки:', error)
        this.handleApiError(error, 'создании этикетки')
      } finally {
        this.isSubmitting = false
      }
    },

    // Редактирование этикетки
    editLabel(label) {
      this.currentLabel = { 
        id: label.id,
        label_name: label.label_name,
        color: label.color
      }
      this.isEditing = true
      window.scrollTo({ top: 0, behavior: 'smooth' })
    },

    // Обновление этикетки
    async updateLabel() {
      if (!this.validateForm()) return

      this.isSubmitting = true
      try {
        console.log('Обновляем этикетку:', this.currentLabel)
        const response = await api.put(
          `/bot_managment/labels/${this.currentLabel.id}`,
          this.currentLabel
        )
        console.log('Ответ от сервера:', response)
        
        const updatedLabel = response.data
        const index = this.labels.findIndex(l => l.id === this.currentLabel.id)
        if (index !== -1) {
          this.labels.splice(index, 1, updatedLabel)
        }
        
        this.showNotification('Этикетка успешно обновлена', 'success', '✅')
        this.resetForm()
      } catch (error) {
        console.error('Ошибка при обновлении этикетки:', error)
        this.handleApiError(error, 'обновлении этикетки')
      } finally {
        this.isSubmitting = false
      }
    },

    // Подтверждение удаления этикетки
    confirmDelete(label) {
      this.labelToDelete = label
      this.showDeleteModal = true
    },

    // Удаление этикетки
    async deleteLabel() {
      if (!this.labelToDelete || this.labelToDelete.product_count > 0) return

      this.isSubmitting = true
      try {
        console.log('Удаляем этикетку:', this.labelToDelete.id)
        const response = await api.delete(`/bot_managment/labels/${this.labelToDelete.id}`)
        console.log('Ответ от сервера:', response)
        
        const index = this.labels.findIndex(l => l.id === this.labelToDelete.id)
        if (index !== -1) {
          this.labels.splice(index, 1)
        }
        
        this.showNotification('Этикетка успешно удалена', 'success', '✅')
      } catch (error) {
        console.error('Ошибка при удалении этикетки:', error)
        this.handleApiError(error, 'удалении этикетки')
      } finally {
        this.isSubmitting = false
        this.cancelDelete()
      }
    },

    // Отмена удаления этикетки
    cancelDelete() {
      this.showDeleteModal = false
      this.labelToDelete = null
    },

    // Сброс формы
    resetForm() {
      this.currentLabel = {
        id: null,
        label_name: '',
        color: '#3B82F6'
      }
      this.isEditing = false
      this.errors = {}
    },

    // Обработка ошибок API
    handleApiError(error, action = 'операции') {
      let errorMessage = `Ошибка при ${action}`
      
      console.log('Обработка ошибки API:', {
        status: error.response?.status,
        data: error.response?.data,
        message: error.message
      })
      
      if (error.response) {
        // Сервер ответил с ошибкой
        const status = error.response.status
        const data = error.response.data
        
        if (status === 400 && data.detail) {
          errorMessage = data.detail
        } else if (status === 404) {
          errorMessage = 'Этикетка не найдена'
        } else if (status === 409) {
          errorMessage = 'Этикетка с таким названием уже существует'
        } else if (status === 422) {
          errorMessage = 'Некорректные данные'
          if (data.detail && Array.isArray(data.detail)) {
            errorMessage = data.detail.map(err => err.msg || err.message).join(', ')
          }
        } else if (status === 500) {
          errorMessage = 'Внутренняя ошибка сервера'
        }
      } else if (error.request) {
        // Запрос был сделан, но ответ не получен
        errorMessage = 'Нет ответа от сервера. Проверьте соединение'
        console.error('Нет ответа от сервера. Проверьте:')
        console.error('1. Работает ли сервер')
        console.error('2. Правильный ли URL:', api.defaults.baseURL)
        console.error('3. Нет ли ошибок CORS')
      } else {
        // Ошибка настройки запроса
        errorMessage = `Ошибка запроса: ${error.message}`
      }
      
      this.showNotification(errorMessage, 'error', '❌')
    },

    // Показ уведомления
    showNotification(message, type = 'success', emoji = '✅') {
      this.notification = {
        show: true,
        message,
        type,
        emoji
      }
      
      setTimeout(() => {
        this.notification.show = false
      }, 5000)
    }
  }
}
</script>

<style scoped>
/* Добавляем стиль для индикатора загрузки в кнопках */
.btn-loading {
  width: 16px;
  height: 16px;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-radius: 50%;
  border-top-color: white;
  animation: spin 1s linear infinite;
  margin: 0 auto;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

/* Обновляем стили для disabled кнопок */
.btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
  transform: none !important;
}

.btn-action:disabled {
  opacity: 0.4;
  cursor: not-allowed;
  transform: none !important;
}

/* Обновляем стиль для состояния загрузки */
.loading-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px 20px;
  text-align: center;
}

.spinner {
  width: 48px;
  height: 48px;
  border: 3px solid rgba(96, 165, 250, 0.3);
  border-radius: 50%;
  border-top-color: #60a5fa;
  animation: spin 1s linear infinite;
  margin-bottom: 16px;
}

/* Остальные стили остаются без изменений */
.labels-management-page {
  max-width: 100%;
  min-height: 100vh;
  padding: 30vw 16px 20px;
  background: linear-gradient(135deg, #0f172a 0%, #1e293b 100%);
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
  color: #f8fafc;
}

/* Заголовок страницы */
.page-header {
  text-align: center;
  margin-bottom: 32px;
  padding: 0 12px;
}

.page-header h1 {
  font-size: 28px;
  font-weight: 700;
  margin-bottom: 8px;
  background: linear-gradient(135deg, #60a5fa 0%, #a78bfa 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}

.page-subtitle {
  font-size: 14px;
  color: #94a3b8;
  margin: 0;
}

/* Карточки */
.form-card,
.labels-list-card {
  background: rgba(30, 41, 59, 0.7);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 20px;
  padding: 24px;
  margin-bottom: 24px;
  box-shadow: 0 10px 25px -5px rgba(0, 0, 0, 0.3);
}

.form-card h2,
.labels-list-card h2 {
  font-size: 20px;
  font-weight: 600;
  margin-bottom: 20px;
  color: #f1f5f9;
}

/* Форма */
.form-grid {
  display: grid;
  gap: 20px;
  margin-bottom: 24px;
}

.form-group {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.form-group label {
  font-size: 14px;
  font-weight: 500;
  color: #cbd5e1;
  display: flex;
  align-items: center;
  gap: 8px;
}

.input-field {
  width: 100%;
  padding: 14px 16px;
  background: rgba(15, 23, 42, 0.8);
  border: 2px solid rgba(255, 255, 255, 0.1);
  border-radius: 12px;
  font-size: 16px;
  color: #f8fafc;
  transition: all 0.3s ease;
}

.input-field:focus {
  outline: none;
  border-color: #60a5fa;
  box-shadow: 0 0 0 3px rgba(96, 165, 250, 0.2);
}

.input-field.error {
  border-color: #f87171;
}

.error-text {
  font-size: 13px;
  color: #f87171;
  margin-top: 4px;
}

/* Выбор цвета */
.color-selection {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 12px;
}

.color-picker {
  width: 50px;
  height: 50px;
  border: none;
  border-radius: 12px;
  cursor: pointer;
  background: transparent;
}

.color-picker::-webkit-color-swatch-wrapper {
  padding: 0;
}

.color-picker::-webkit-color-swatch {
  border: 3px solid rgba(255, 255, 255, 0.2);
  border-radius: 10px;
}

.color-value {
  font-family: 'Courier New', monospace;
  font-size: 14px;
  color: #94a3b8;
  padding: 6px 12px;
  background: rgba(15, 23, 42, 0.8);
  border-radius: 8px;
}

.color-presets {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
}

.color-preset {
  width: 32px;
  height: 32px;
  border-radius: 8px;
  border: 2px solid transparent;
  cursor: pointer;
  transition: all 0.2s ease;
}

.color-preset:hover {
  transform: scale(1.1);
  border-color: white;
}

/* Кнопки */
.form-actions {
  display: flex;
  gap: 12px;
}

.btn {
  flex: 1;
  padding: 14px 24px;
  border: none;
  border-radius: 12px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
}

.btn-primary {
  background: linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%);
  color: white;
}

.btn-primary:disabled {
  background: #475569;
  cursor: not-allowed;
  opacity: 0.6;
}

.btn-secondary {
  background: rgba(255, 255, 255, 0.1);
  color: #cbd5e1;
  border: 1px solid rgba(255, 255, 255, 0.2);
}

.btn-danger {
  background: linear-gradient(135deg, #ef4444 0%, #dc2626 100%);
  color: white;
}

.btn:not(:disabled):hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 12px -2px rgba(0, 0, 0, 0.3);
}

/* Поиск */
.list-header {
  display: flex;
  flex-direction: column;
  gap: 16px;
  margin-bottom: 20px;
}

.search-input {
  width: 100%;
  padding: 14px 16px;
  padding-left: 44px;
  background: rgba(15, 23, 42, 0.8);
  border: 2px solid rgba(255, 255, 255, 0.1);
  border-radius: 12px;
  font-size: 16px;
  color: #f8fafc;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='20' height='20' viewBox='0 0 24 24' fill='none' stroke='%2394a3b8' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Ccircle cx='11' cy='11' r='8'%3E%3C/circle%3E%3Cline x1='21' y1='21' x2='16.65' y2='16.65'%3E%3C/line%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: 16px center;
}

/* Состояния загрузки и пустого списка */
.loading-state,
.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px 20px;
  text-align: center;
}

.spinner {
  width: 48px;
  height: 48px;
  border: 3px solid rgba(96, 165, 250, 0.3);
  border-radius: 50%;
  border-top-color: #60a5fa;
  animation: spin 1s linear infinite;
  margin-bottom: 16px;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

.empty-emoji {
  font-size: 48px;
  margin-bottom: 16px;
  opacity: 0.5;
}

/* Список этикеток */
.labels-grid {
  display: grid;
  gap: 12px;
}

.label-card {
  background: rgba(15, 23, 42, 0.8);
  border-radius: 16px;
  padding: 20px;
  border-left: 4px solid;
  display: flex;
  flex-direction: column;
  gap: 16px;
  transition: all 0.3s ease;
}

.label-card:hover {
  transform: translateY(-2px);
  background: rgba(15, 23, 42, 0.9);
}

.label-info {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.label-badge {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 8px 16px;
  border-radius: 12px;
  max-width: fit-content;
  font-weight: 600;
  font-size: 15px;
}

.label-name {
  color: white;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.2);
}

.label-color {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  color: #94a3b8;
}

.color-dot {
  width: 16px;
  height: 16px;
  border-radius: 50%;
  border: 2px solid rgba(255, 255, 255, 0.2);
}

.label-stats {
  font-size: 13px;
  color: #64748b;
}

.stats-item {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 4px 12px;
  background: rgba(30, 41, 59, 0.5);
  border-radius: 8px;
}

.label-actions {
  display: flex;
  gap: 8px;
  justify-content: flex-end;
}

.btn-action {
  width: 44px;
  height: 44px;
  border: none;
  border-radius: 12px;
  font-size: 18px;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
}

.edit-btn {
  background: rgba(96, 165, 250, 0.2);
  color: #60a5fa;
}

.delete-btn {
  background: rgba(239, 68, 68, 0.2);
  color: #ef4444;
}

.delete-btn:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}

.btn-action:not(:disabled):hover {
  transform: scale(1.1);
}

/* Модальное окно */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.8);
  backdrop-filter: blur(4px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 20px;
}

.modal-content {
  background: #1e293b;
  border-radius: 20px;
  padding: 24px;
  max-width: 400px;
  width: 100%;
  border: 1px solid rgba(255, 255, 255, 0.1);
  box-shadow: 0 20px 40px -10px rgba(0, 0, 0, 0.5);
}

.modal-header h3 {
  font-size: 20px;
  font-weight: 600;
  margin-bottom: 16px;
  color: #f1f5f9;
}

.modal-body {
  margin-bottom: 24px;
  color: #cbd5e1;
  line-height: 1.5;
}

.warning-text {
  color: #f87171;
  font-size: 14px;
  margin-top: 12px;
  display: flex;
  align-items: center;
  gap: 8px;
}

.modal-actions {
  display: flex;
  gap: 12px;
}

/* Уведомления */
.notification {
  position: fixed;
  bottom: 24px;
  left: 50%;
  transform: translateX(-50%);
  background: rgba(30, 41, 59, 0.95);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 16px;
  padding: 16px 20px;
  display: flex;
  align-items: center;
  gap: 12px;
  min-width: 280px;
  max-width: 90%;
  box-shadow: 0 10px 25px -5px rgba(0, 0, 0, 0.3);
  z-index: 1001;
  animation: slideUp 0.3s ease;
}

@keyframes slideUp {
  from {
    opacity: 0;
    transform: translate(-50%, 20px);
  }
  to {
    opacity: 1;
    transform: translate(-50%, 0);
  }
}

.notification.success {
  border-left: 4px solid #10b981;
}

.notification.error {
  border-left: 4px solid #ef4444;
}

.notification-emoji {
  font-size: 20px;
}

.notification-text {
  flex: 1;
  font-size: 14px;
  color: #f8fafc;
}

.notification-close {
  background: none;
  border: none;
  color: #94a3b8;
  cursor: pointer;
  font-size: 14px;
  padding: 4px;
  transition: color 0.2s ease;
}

.notification-close:hover {
  color: #f8fafc;
}

/* Адаптивность */
@media (max-width: 480px) {
  .labels-management-page {
    padding: 30vw 12px 20px;
  }
  
  .form-card,
  .labels-list-card {
    padding: 20px 16px;
  }
  
  .page-header h1 {
    font-size: 24px;
  }
  
  .btn {
    padding: 12px 20px;
    font-size: 15px;
  }
}

@media (max-width: 360px) {
  .labels-management-page {
    padding: 30vw 8px 16px;
  }
  
  .label-card {
    padding: 16px;
  }
  
  .btn-action {
    width: 40px;
    height: 40px;
  }
}
</style>